import {
  ButtonItem,
  ButtonOptions,
  CustomRule,
  Form as DxForm,
  FormRef,
  GroupItem,
  NumericRule,
  RequiredRule,
  SimpleItem,
  StringLengthRule,
  Tab,
  TabbedItem,
  TabPanelOptions,
} from 'devextreme-react/form'
import type { FieldDataChangedEvent } from 'devextreme/ui/form'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useRevalidator } from 'react-router-dom'
import { FormMode } from '@/enums'
import { ArraySourceComposer } from '@/auth/api/connector'
import { SoftwareAdskEditorFormProps } from '@/routes/software/adsk/SoftwareAdskEditor.types'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import notify from 'devextreme/ui/notify'
import { customButton } from '@/routes/utils'
import { ClickEvent } from 'devextreme/ui/button'
import QuotesBriefGridMemoed from '@/routes/projects/QuotesBriefGrid'
import { ad_csn, azienda } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { ValueChangedEvent as ValueChangedEventSelectBox } from 'devextreme/ui/select_box'
import { DataSource } from 'devextreme/common/data'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { useScreenSize } from '@/themes/media-query'

export const SoftwareAdskEditorForm = (props: SoftwareAdskEditorFormProps) => {
  const { license, mode, aziendaList, productsList, isRoleAllowed, fetcher } = props
  const revalidator = useRevalidator()
  const formAdskRef = useRef<FormRef>(null)
  const today = new Date().toISOString()
  const [productsAdSubscriptionList, setProductsAdSubscriptionList] = useState<(string | null)[]>([])
  const { clientId } = useParams()
  const currentScreenSize = useScreenSize()
  const qsAdminApi = useQsAdminApiManager.getState().service
  const [currentCompany, setCurrentCompany] = useState<azienda | null | undefined>(
    clientId ? aziendaList[0] : license && 'csn' in license ? license?.csn?.azienda : undefined,
  )
  const [currentCompanyChanged, setCurrentCompanyChanged] = useState<boolean>(
    clientId && mode === FormMode.Create ? true : false,
  )

  const [csnDataSource, setCsnDataSource] = useState<DataSource>(
    ArraySourceComposer('id', license && 'csn' in license ? license?.csn?.azienda?.ad_csns : undefined),
  )

  const getCompanyCsns = useCallback(async () => {
    const csnsData = await qsAdminApi.ad_csn().query((builder, csn) => {
      builder.filter(csn.azienda.props.id.eq(Number(currentCompany?.id)))
      builder.orderBy(csn.csn.desc())
      builder.expanding('azienda', (companyBuilder) => {
        companyBuilder.expand('ad_csns')
        companyBuilder.select('id', 'nome', 'ad_csns')
      })
    })
    return csnsData.data.value
  }, [currentCompany?.id, qsAdminApi])

  const handleFieldDataChanged = (e: FieldDataChangedEvent) => {
    console.log('FORMDATACHANGED EVENT')
    if (
      formAdskRef.current?.instance().option().isDirty !== null &&
      formAdskRef.current?.instance().option().isDirty !== undefined
    ) {
      console.log('is dirty', formAdskRef.current?.instance().option().isDirty)
      formAdskRef.current
        .instance()
        .getButton('btn-save')
        ?.option('disabled', !formAdskRef.current?.instance().option().isDirty)
      formAdskRef.current
        .instance()
        .getButton('btn-cancel')
        ?.option('disabled', !formAdskRef.current?.instance().option().isDirty)
    }
  }

  useEffect(() => {
    if (!currentCompanyChanged) return
    console.log('COMPANY EFFECT', currentCompany)
    if (currentCompany) {
      getCompanyCsns().then((csns: ad_csn[]) => {
        console.log('csns', csns)
        setCsnDataSource(ArraySourceComposer('id', csns))
        formAdskRef.current?.instance().updateData('csn', csns[0])
      })
      setCurrentCompanyChanged(false)
    }
  }, [currentCompanyChanged, currentCompany, getCompanyCsns])

  useEffect(() => {
    const tempList: (string | null)[] = []
    productsList.forEach((product) => {
      tempList.push(product.offeringName)
    })
    console.log('tempList', tempList)
    setProductsAdSubscriptionList([
      ...new Map(tempList.map((productName: string | null) => [productName, productName])).values(),
    ])
  }, [productsList])

  return (
    <>
      <LoadingPanel
        visible={!((fetcher?.state === 'idle' || mode === FormMode.View) && revalidator.state === 'idle')}
      />
      <DxForm
        id={`adsk_editor_form ${license && 'id' in license ? license?.id : 'new'}`}
        key={`adsk_editor_form ${license && 'id' in license ? license?.id : 'new'}`}
        formData={license}
        ref={formAdskRef}
        onFieldDataChanged={handleFieldDataChanged}
        validationGroup={`adskLicenseDataValidation ${license && 'id' in license ? license?.id : 'new'}`}
        readOnly={mode === FormMode.View || (mode === FormMode.Edit && !license?.manualEntry) || !isRoleAllowed}
      >
        <GroupItem colCountByScreen={{ xs: 1, sm: 2, md: 2, lg: 2 }}>
          <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 2 }}>
            <SimpleItem
              label={{ text: 'Azienda' }}
              editorType="dxSelectBox"
              editorOptions={{
                buttons: [
                  customButton('open', (e: ClickEvent) => {
                    if (currentCompany) window.open(`/clients/${currentCompany.id}`, '_blank')
                    else notify(`Deve essere selezionata un' azienda per poterla aprire.`, 'warning', 3000)
                  }),
                  { name: 'dropDown', location: 'after' },
                ],
                value: currentCompany,
                dataSource: ArraySourceComposer('id', aziendaList),
                placeholder: "Seleziona l'azienda...",
                displayExpr: 'nome',
                searchEnabled: true,
                dropDownCentered: true,
                dropDownOptions: {
                  showTitle: true,
                  title: "Selezionare l'azienda",
                  hideOnOutsideClick: true,
                },
                onValueChanged: (e: ValueChangedEventSelectBox) => {
                  if (e.previousValue?.id === e.value?.id) return
                  console.log('COMPANY EVENT CHANGED', e.previousValue, e.value)
                  setCurrentCompany(e.value)
                  setCurrentCompanyChanged(true)
                },
                readOnly:
                  mode === FormMode.View ||
                  (mode === FormMode.Edit && !license?.manualEntry) ||
                  !isRoleAllowed ||
                  clientId
                    ? true
                    : false,
              }}
            >
              {mode === FormMode.Create && <RequiredRule message="Azienda obbligatoria"></RequiredRule>}
            </SimpleItem>
            <SimpleItem
              dataField="csn"
              label={{ text: 'Csn' }}
              editorType="dxSelectBox"
              editorOptions={{
                buttons: [
                  {
                    name: 'clear',
                    location: 'after',
                  },
                  { name: 'dropDown', location: 'after' },
                ],
                dataSource: csnDataSource,
                placeholder: 'Seleziona il csn...',
                displayExpr: 'csn',
                searchEnabled: true,
                showDropDownButton: true,
                dropDownButtonTemplate: 'dropDownButton',
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare il sede',
                  hideOnOutsideClick: true,
                },
              }}
            >
              {mode === FormMode.Create && <RequiredRule message="Csn obbligatorio"></RequiredRule>}
            </SimpleItem>
            <SimpleItem dataField="subscriptionId" label={{ text: 'Subscription Id' }}>
              {mode === FormMode.Create && (
                <>
                  <RequiredRule message="Subscription Id obbligatorio"></RequiredRule>
                  <NumericRule
                    ignoreEmptyValue={true}
                    message="Subscription Id deve essere composto da sole cifre numeriche"
                  ></NumericRule>
                  <StringLengthRule
                    ignoreEmptyValue={true}
                    max={14}
                    min={14}
                    message="Subscription Id deve essere di 14 cifre"
                  ></StringLengthRule>
                </>
              )}
            </SimpleItem>
            <SimpleItem dataField="serialNumber" label={{ text: 'Seriale' }} />
            <SimpleItem
              dataField="startDate"
              editorOptions={{
                displayFormat: 'dd/MM/yyyy',
              }}
              editorType="dxDateBox"
              label={{ text: 'Inizio' }}
            >
              {mode === FormMode.Create && (
                <>
                  <RequiredRule message="Data inizio obbligatoria"></RequiredRule>
                  <CustomRule
                    reevaluate={true}
                    message="Data inizio deve essere minore di data fine"
                    validationCallback={(options: {
                      column: Record<string, any>
                      data: Record<string, any>
                      formItem: Record<string, any>
                      rule: Record<string, any>
                      validator: Record<string, any>
                      value: string | number
                    }) => {
                      console.log('value', new Date(options.value))
                      console.log('fine', new Date(license?.endDate ?? ''))
                      return new Date(options.value) < new Date(license?.endDate ?? '')
                    }}
                  ></CustomRule>
                </>
              )}
            </SimpleItem>
            <SimpleItem
              dataField="endDate"
              editorOptions={{
                displayFormat: 'dd/MM/yyyy',
              }}
              editorType="dxDateBox"
              label={{ text: 'Fine' }}
            >
              {mode === FormMode.Create && (
                <>
                  <RequiredRule message="Data fine obbligatoria"></RequiredRule>
                  <CustomRule
                    reevaluate={true}
                    message="Data fine deve essere maggiore di data inizio"
                    validationCallback={(options: {
                      column: Record<string, any>
                      data: Record<string, any>
                      formItem: Record<string, any>
                      rule: Record<string, any>
                      validator: Record<string, any>
                      value: string | number
                    }) => {
                      console.log('value', new Date(options.value))
                      console.log('inizio', new Date(license?.startDate ?? ''))
                      return new Date(options.value) > new Date(license?.startDate ?? '')
                    }}
                  ></CustomRule>
                </>
              )}
            </SimpleItem>
            <SimpleItem
              dataField="productName"
              label={{ text: 'Prodotto' }}
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: productsAdSubscriptionList,
                placeholder: 'Seleziona prodotto...',
                searchEnabled: true,
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare prodotto',
                  hideOnOutsideClick: true,
                },
              }}
            >
              {mode === FormMode.Create && <RequiredRule message="Prodotto obbligatorio"></RequiredRule>}
            </SimpleItem>
            <SimpleItem dataField="release" label={{ text: 'Versione' }} editorOptions={{ readOnly: true }} />
            <SimpleItem dataField="licenseType" label={{ text: 'Licenza' }} editorOptions={{ readOnly: true }} />
            <SimpleItem
              dataField="status"
              label={{ text: 'Stato' }}
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: [
                  'Active',
                  'Canceled',
                  'Expired',
                  'Inactive',
                  'Registered',
                  'Suspended',
                  'Upgraded',
                  'VAR Changed',
                ],
                placeholder: 'Seleziona stato...',
                searchEnabled: true,
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare stato',
                  hideOnOutsideClick: true,
                },
                readOnly: true,
              }}
            >
              {mode === FormMode.Create && <RequiredRule message="Stato obbligatorio"></RequiredRule>}
            </SimpleItem>
            <SimpleItem
              dataField="quantity"
              label={{ text: 'Postazioni' }}
              editorType="dxNumberBox"
              editorOptions={{
                min: 0,
                max: 9999,
                showSpinButtons: true,
              }}
            >
              {mode === FormMode.Create && (
                <>
                  <RequiredRule message="Postazioni obbligatorio"></RequiredRule>
                  <CustomRule
                    reevaluate={true}
                    message="Postazioni deve essere maggiore di 0"
                    validationCallback={(options: {
                      column: Record<string, any>
                      data: Record<string, any>
                      formItem: Record<string, any>
                      rule: Record<string, any>
                      validator: Record<string, any>
                      value: string | number
                    }) => {
                      return Number(options.value) > 0
                    }}
                  ></CustomRule>
                </>
              )}
            </SimpleItem>
            <SimpleItem dataField="switchType" label={{ text: 'Switch' }} editorOptions={{ readOnly: true }} />
            <SimpleItem dataField="contract" label={{ text: 'Contratto' }} editorOptions={{ readOnly: true }} />
            <SimpleItem
              dataField="term"
              label={{ text: 'Termine' }}
              editorType="dxSelectBox"
              editorOptions={{
                dataSource: ['Monthly', 'Quarterly', 'Annual', '2-Year', '3 Year'],
                placeholder: 'Seleziona termine...',
                searchEnabled: true,
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare termine',
                  hideOnOutsideClick: true,
                },
              }}
            >
              {mode === FormMode.Create && <RequiredRule message="Termine obbligatorio"></RequiredRule>}
            </SimpleItem>
          </GroupItem>
          <GroupItem colCount={1}>
            <TabbedItem>
              <TabPanelOptions
                animationEnabled={true}
                swipeEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall}
                deferRendering={false}
              />
              <Tab title="Note">
                <SimpleItem
                  dataField={'note'}
                  editorType={'dxTextArea'}
                  label={{ visible: false }}
                  editorOptions={{
                    height: '440',
                    readOnly: mode === FormMode.View || !isRoleAllowed,
                  }}
                ></SimpleItem>
              </Tab>
              <Tab title="Opzioni">
                <GroupItem colCount={2}>
                  <SimpleItem
                    dataField="extensionDate"
                    editorOptions={{
                      displayFormat: 'dd/MM/yyyy',
                      defaultValue: { today },
                      type: 'date',
                      pickerType: 'calendar',
                      applyValueMode: 'instantly',
                      showClearButton: true,
                      readOnly: mode === FormMode.View || !isRoleAllowed,
                    }}
                    colSpan={1}
                    editorType="dxDateBox"
                    label={{ text: 'Estensione' }}
                  ></SimpleItem>
                  <SimpleItem editorType="dxCheckBox" dataField="autoRenew" label={{ text: 'Auto Rinnovo' }} />
                </GroupItem>
              </Tab>
              <Tab title="Quote">
                <QuotesBriefGridMemoed licenseAdsk={license} readOnly={true}></QuotesBriefGridMemoed>
              </Tab>
            </TabbedItem>
          </GroupItem>
        </GroupItem>
        <GroupItem visible={isRoleAllowed} cssClass="last-group">
          <GroupItem cssClass="buttons-group" colCountByScreen={{ xs: 1, sm: 2, md: 2, lg: 2 }}>
            {(mode === FormMode.Edit || mode === FormMode.Create) && (
              <ButtonItem name="btn-save">
                <ButtonOptions
                  disabled={!formAdskRef.current?.instance().option().isDirty}
                  icon="save"
                  text="Salva"
                  width="150px"
                  onClick={async () => {
                    const validationResult = formAdskRef.current?.instance().validate()
                    if (!validationResult?.isValid) return
                    fetcher?.submit(JSON.stringify(license), {
                      method: mode === FormMode.Create ? 'POST' : 'PATCH',
                      encType: 'application/json',
                    })
                  }}
                />
              </ButtonItem>
            )}
            {mode === FormMode.Edit && (
              <ButtonItem
                name="btn-cancel"
                buttonOptions={{
                  disabled: !formAdskRef.current?.instance().option().isDirty,
                  icon: 'undo',
                  text: 'Annulla',
                  width: '150px',
                  onClick() {
                    revalidator.revalidate()
                  },
                }}
              ></ButtonItem>
            )}
          </GroupItem>
        </GroupItem>
      </DxForm>
    </>
  )
}
